import { AUDIO_MONITOR_DEFAULTS } from './constants';
import { audioLatencyStore } from '../../zustand/audio-latency-store';
import { publishMetricEvent } from './metrics-bridge';

// `connectionState === "connected"` does NOT mean the user can hear anything —
// the peer connection can be healthy while the stream is silent. This monitor
// is the only honest source for "is the agent audibly talking right now", and
// it also timestamps the first audible sample for latency measurement.
//
// It is a pure detector: it reports, it never acts. Whether a reported anomaly
// results in anything (observe vs heal) is the store's decision.

const getAudioContextCtor = () =>
  typeof window === 'undefined' ? null : window.AudioContext || window.webkitAudioContext;

// getFloatFrequencyData writes -Infinity into bins the analyser considers
// empty. Left as-is they poison every mean, so they are pinned to a floor well
// below analyserMinDecibels.
const DB_FLOOR = -140;

// Bins on either side of the peak excluded from the "rest of the spectrum"
// mean. A real windowed tone is not one bin wide — it leaks into its
// neighbours, and counting that skirt as floor would understate prominence.
const PEAK_SKIRT_BINS = 3;

function calculateStats(values) {
  if (!values.length) {
    return null;
  }

  const sorted = [...values].sort((a, b) => a - b);
  const count = sorted.length;
  const mean = sorted.reduce((sum, value) => sum + value, 0) / count;
  const variance = sorted.reduce((sum, value) => sum + (value - mean) ** 2, 0) / count;

  return {
    count,
    min: sorted[0],
    max: sorted[count - 1],
    mean,
    std: Math.sqrt(variance),
    median:
      count % 2 === 0 ? (sorted[count / 2 - 1] + sorted[count / 2]) / 2 : sorted[(count - 1) / 2],
  };
}

export function createAudioSignalMonitor({
  onAudibilityChange,
  onPlaybackDetected,
  onAnomalyDetected,
  options = {},
}) {
  const config = { ...AUDIO_MONITOR_DEFAULTS, ...options };

  let audioContext = null;
  let sourceNode = null;
  let analyserNode = null;
  let sampleBuffer = null;
  let freqBuffer = null;
  let intervalId = null;

  let activeSamples = 0;
  let silentSamples = 0;
  let isAudible = false;
  // Kept for the debug report: the raw loudness, before the debounce that turns
  // it into the audible/not flag.
  let lastRms = 0;

  // Gap between the last two ticks vs the nominal sampleIntervalMs. A setInterval
  // callback that lands 300ms late means the main thread is starved — the CPU
  // cross-check for the "decode glitch under CPU pressure" theory, and the thing
  // that separates "the audio path broke" from "the whole tab froze".
  let lastTickAt = null;
  let lastTickLagMs = 0;

  // Tone-run state. `toneRun` is the number of consecutive qualifying samples;
  // the rest describe the run in progress and are only meaningful while it is
  // non-zero. Reset on teardown like the audible-flag counters above.
  let toneRun = 0;
  let toneAnchorBin = 0;
  let toneOnsetAt = 0;
  let toneRmsMin = Infinity;
  let toneRmsMax = 0;
  let toneProminenceSum = 0;
  // NOT reset on teardown. A heal tears the graph down and rebuilds it, so
  // resetting this there would defeat the cooldown on exactly the cycle it
  // exists to limit. (Belt-and-braces since the heal no longer stops the
  // monitor, but the invariant should not depend on that.)
  let lastAnomalyAt = -Infinity;

  const setAudible = next => {
    if (next === isAudible) {
      return;
    }
    isAudible = next;
    onAudibilityChange?.(next);
  };

  const resetToneRun = () => {
    toneRun = 0;
    toneRmsMin = Infinity;
    toneRmsMax = 0;
    toneProminenceSum = 0;
  };

  /**
   * Flags a sustained, spectrally-stable dominant peak — the signature of a
   * runaway tone glitch, as opposed to speech.
   *
   * Four independent gates, in rough order of how much work they do:
   *
   *  1. Duration. Speech does not hold one spectral configuration for 1.2s.
   *     This is the real defence; the others mostly stop it firing on the way.
   *  2. Peak-bin stability, anchored to the run's first sample. A tone sits
   *     still; formants migrate.
   *  3. Peak prominence in dB over the mean of the rest of the spectrum. Level
   *     invariant, and unlike a single-bin energy share it survives the
   *     harmonic comb a repeating Opus frame produces.
   *  4. RMS flatness. Speech has syllabic amplitude modulation; a tone does not.
   *
   * See AUDIO_MONITOR_DEFAULTS for the reasoning behind each threshold. Reuses
   * the same analyser as the RMS check below; no extra AudioContext work.
   */
  const checkToneAnomaly = rms => {
    if (!analyserNode || !freqBuffer || !audioContext) {
      return;
    }

    // Nothing to misread in silence/noise floor — only worth checking once
    // something is actually audible.
    if (rms <= config.signalThreshold) {
      resetToneRun();
      return;
    }

    // Float, not byte: getByteFrequencyData returns dB linearly remapped into
    // 0-255 against min/maxDecibels, which quantizes the very differences this
    // is measuring and clamps loud bins to a shared ceiling. Raw dB has neither
    // problem.
    analyserNode.getFloatFrequencyData(freqBuffer);

    const binCount = freqBuffer.length;
    let peakBin = 0;
    let peakDb = -Infinity;
    for (let i = 0; i < binCount; i += 1) {
      const value = Number.isFinite(freqBuffer[i]) ? freqBuffer[i] : DB_FLOOR;
      if (value > peakDb) {
        peakDb = value;
        peakBin = i;
      }
    }

    let floorSum = 0;
    let floorCount = 0;
    for (let i = 0; i < binCount; i += 1) {
      if (Math.abs(i - peakBin) <= PEAK_SKIRT_BINS) {
        continue;
      }
      floorSum += Number.isFinite(freqBuffer[i]) ? freqBuffer[i] : DB_FLOOR;
      floorCount += 1;
    }

    if (!floorCount) {
      resetToneRun();
      return;
    }

    const prominenceDb = peakDb - floorSum / floorCount;
    const nyquistHz = audioContext.sampleRate / 2;
    const peakFrequencyHz = (peakBin / binCount) * nyquistHz;

    const looksLikeTone =
      peakFrequencyHz >= config.toneMinFrequencyHz && prominenceDb >= config.tonePeakProminenceDb;

    if (!looksLikeTone) {
      resetToneRun();
      return;
    }

    // Anchored to the run's first sample, not the previous one: a peak drifting
    // by the tolerance every sample would otherwise walk arbitrarily far while
    // never failing the check.
    if (toneRun === 0) {
      toneAnchorBin = peakBin;
      toneOnsetAt = performance.now();
    } else if (Math.abs(peakBin - toneAnchorBin) > config.tonePeakBinTolerance) {
      resetToneRun();
      return;
    }

    toneRun += 1;
    toneRmsMin = Math.min(toneRmsMin, rms);
    toneRmsMax = Math.max(toneRmsMax, rms);
    toneProminenceSum += prominenceDb;

    if (toneRun < config.toneConsecutiveSamplesRequired) {
      return;
    }

    // Checked only once the run is otherwise complete: a single loud sample
    // early on should not disqualify a run that turns out to be flat, and the
    // ratio is meaningless before there are samples to compare.
    const rmsRatio = toneRmsMin > 0 ? toneRmsMax / toneRmsMin : Infinity;
    if (rmsRatio > config.toneRmsStabilityRatio) {
      resetToneRun();
      return;
    }

    const snapshot = {
      peakBin,
      peakFrequencyHz,
      prominenceDb,
      meanProminenceDb: toneProminenceSum / toneRun,
      heldForMs: toneRun * config.sampleIntervalMs,
      onsetAt: toneOnsetAt,
      rms,
      rmsMin: toneRmsMin,
      rmsMax: toneRmsMax,
      tickLagMs: lastTickLagMs,
    };

    resetToneRun();

    const now = performance.now();
    if (now - lastAnomalyAt < config.toneHealCooldownMs) {
      return;
    }
    lastAnomalyAt = now;

    onAnomalyDetected?.(snapshot);
  };

  const sampleSignal = () => {
    const now = performance.now();
    lastTickLagMs = lastTickAt === null ? 0 : now - lastTickAt - config.sampleIntervalMs;
    lastTickAt = now;

    if (!analyserNode || !sampleBuffer) {
      return;
    }

    analyserNode.getFloatTimeDomainData(sampleBuffer);

    let sumSquares = 0;
    for (let i = 0; i < sampleBuffer.length; i += 1) {
      sumSquares += sampleBuffer[i] ** 2;
    }
    const rms = Math.sqrt(sumSquares / sampleBuffer.length);
    lastRms = rms;

    checkToneAnomaly(rms);

    if (rms > config.signalThreshold) {
      // Rising edge = the first audible sample of this reply. That's the moment
      // the user actually hears the agent, so it's what latency is measured to.
      const risingEdge = !audioLatencyStore.signalPresent;
      audioLatencyStore.signalPresent = true;

      if (risingEdge && audioLatencyStore.firstAudiblePending && audioLatencyStore.queue.length) {
        const current = audioLatencyStore.queue.shift();
        const latencyMs = performance.now() - current.startedAt;
        audioLatencyStore.latencies.push(latencyMs);
        audioLatencyStore.firstAudiblePending = false;

        publishMetricEvent({ type: 'first-audible', latencyMs });
        onPlaybackDetected?.('audio.signal');

        if (audioLatencyStore.latencies.length % 50 === 0) {
          console.warn(
            '[realtime-audio] latency stats',
            calculateStats(audioLatencyStore.latencies)
          );
        }
      }

      silentSamples = 0;
      activeSamples += 1;
      if (activeSamples >= config.activeSamplesRequired) {
        setAudible(true);
      }
      return;
    }

    if (audioLatencyStore.signalPresent) {
      audioLatencyStore.signalPresent = false;
    }

    activeSamples = 0;
    silentSamples += 1;
    if (silentSamples >= config.silenceSamplesBeforeMute) {
      setAudible(false);
    }
  };

  const teardownGraph = () => {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }

    activeSamples = 0;
    silentSamples = 0;
    sampleBuffer = null;
    freqBuffer = null;
    lastTickAt = null;
    resetToneRun();

    try {
      sourceNode?.disconnect();
    } catch (_error) {
      // Already disconnected.
    }
    try {
      analyserNode?.disconnect();
    } catch (_error) {
      // Already disconnected.
    }
    try {
      audioContext?.close();
    } catch (_error) {
      // Already closed. Leaking AudioContexts is the thing to avoid here.
    }

    sourceNode = null;
    analyserNode = null;
    audioContext = null;
  };

  return {
    async start(stream) {
      teardownGraph();

      const AudioContextCtor = getAudioContextCtor();
      if (!AudioContextCtor || !stream) {
        return;
      }

      audioContext = new AudioContextCtor();
      sourceNode = audioContext.createMediaStreamSource(stream);
      analyserNode = audioContext.createAnalyser();
      analyserNode.fftSize = config.fftSize;
      // Non-default on purpose — see AUDIO_MONITOR_DEFAULTS. Smoothing in
      // particular is load-bearing: at the 0.8 default the peak-bin stability
      // check measures the smoother rather than the signal.
      analyserNode.smoothingTimeConstant = config.analyserSmoothingTimeConstant;
      analyserNode.minDecibels = config.analyserMinDecibels;
      analyserNode.maxDecibels = config.analyserMaxDecibels;
      sourceNode.connect(analyserNode);
      sampleBuffer = new Float32Array(analyserNode.fftSize);
      freqBuffer = new Float32Array(analyserNode.frequencyBinCount);

      // Autoplay policy can hand back a suspended context.
      if (audioContext.state === 'suspended') {
        await audioContext.resume().catch(() => {});
      }

      intervalId = window.setInterval(sampleSignal, config.sampleIntervalMs);
    },

    /**
     * Un-suspends the AudioContext. The autoplay policy hands back a suspended
     * context when the graph is built before the document has been interacted
     * with — which is now the normal case, since the call opens on the camera
     * page before the user clicks anything. A suspended context reads RMS 0
     * forever, so `openAiVoiceAudible` (and the `isAudioRunning` flag the
     * backend throttles on) would stay false through an audible reply.
     */
    async resume() {
      if (!audioContext || audioContext.state !== 'suspended') {
        return;
      }
      await audioContext.resume().catch(() => {});
    },

    stop() {
      teardownGraph();
      setAudible(false);
    },

    isSignalPresent() {
      return isAudible;
    },

    getLastRms() {
      return lastRms;
    },

    getLastTickLagMs() {
      return lastTickLagMs;
    },
  };
}
